import AttachmentAnswer from "../attachments/AttachmentAnswer";
import { MessageFiles, errorText } from "../attachments/AttachmentPanel";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BotAvatarIcon } from "../../AiChat/components/Icons";
import { BouncingDots } from "../../AiChat/components/ChatComponents";
import {
  MessageRenderer,
  ActionLinks,
  extractActionLinks,
  cleanMessageText,
} from "../../../../components/MessageWithTableRenderer";
import VideoCards, { stripVideoList } from "./VideoCards";

// Message presentation lives here: content selection, citations, and progress.
// Video presentation and user actions have their own feature modules.
export default function MessageBubble({
  message,
  language,
  actions,
  attachmentFiles = [],
  fileCardPrefix,
}) {
  if (message.role === "user") {
    // Attached files sit above the text, outside the orange bubble; a message
    // with only files has no bubble at all.
    return (
      <div className="flex flex-col items-end gap-1.5 mb-2 md:mb-4">
        {message.files?.length > 0 && (
          <MessageFiles
            files={message.files}
            current={attachmentFiles}
            targetPrefix={message.id}
            language={language}
          />
        )}
        {message.text && (
          <div
            className="max-w-[85%] md:max-w-[70%] bg-orange-500 text-white p-2 md:p-3 rounded-bl-3xl rounded-tl-3xl rounded-br-3xl shadow-sm break-words"
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            <span className="text-xs md:text-sm leading-relaxed whitespace-pre-wrap">
              {message.text}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="text-xs md:text-sm flex gap-2 md:gap-3 items-start mb-2 md:mb-4">
      <BotAvatarIcon />
      <div className="flex-1 min-w-0 mt-2">
        <div className="font-medium leading-relaxed break-words text-black dark:text-white">
          {message.status === "loading" && (
            <StageIndicator
              stage={message.stage}
              language={language}
              attachmentTurn={message.attachmentTurn}
            />
          )}
          {(message.status === "streaming" ||
            message.status === "done" ||
            (message.attachmentTurn && message.status === "error")) && (
            <>
              {message.attachmentTurn ? (
                <AttachmentAnswer
                  message={message}
                  fileCardPrefix={fileCardPrefix}
                  language={language}
                />
              ) : needsMarkdown(message) ? (
                <AnswerText
                  text={stripVideoList(message)}
                  sources={message.sources}
                />
              ) : (
                <MessageRenderer text={message.text} language={language} />
              )}
              <VideoCards videos={message.videos} language={language} />
              {!message.attachmentTurn && (
                <WebSearchNotice worker={message.worker} language={language} />
              )}
            </>
          )}
          {/* A second content block may still be on its way (spec §3.3) — the
              first one is already readable, so keep the dots running below it. */}
          {message.status === "streaming" && (
            <div className="mt-2">
              <StageIndicator
                stage={
                  message.attachmentTurn
                    ? message.stage || "composing"
                    : "composing"
                }
                language={language}
                attachmentTurn={message.attachmentTurn}
              />
            </div>
          )}
          {message.status === "error" && (
            <div className="text-red-600 dark:text-red-400">
              {language === "zh" ? "發生錯誤：" : "Something went wrong:"}{" "}
              {message.errorDetail
                ? errorText(message.errorDetail, language)
                : message.errorText}
            </div>
          )}
        </div>
        {actions}
        <span className="inline-block mt-1 text-[10px] text-slate-400 dark:text-gray-500 tabular-nums">
          <ElapsedTimer
            startedAt={message.startedAt}
            elapsedMs={message.elapsedMs}
          />
        </span>
      </div>
    </div>
  );
}

// FAQ-path answers cite sources inline as [1], [2]... — rewrite those markers
// into markdown links so react-markdown renders them as badges alongside the
// prose. Sourced from FAQ's FaqChatbot.js AnswerText (same citation format,
// same backend), trimmed to badge-only — no image/domain cards here (2026-09-01
// scope decision, see frontend_ui_gap_findings_2026_09_01.md).
function AnswerText({ text, sources }) {
  // An answer can be markdown prose AND end with a `Source:` block. The block is
  // ours, not markdown, so it is cut from the text and rendered as the same
  // source buttons MessageRenderer produces — otherwise it would print as a bare
  // list of URLs below the answer.
  const actionLinks = extractActionLinks(text);
  const body = actionLinks.length > 0 ? cleanMessageText(text) : text;
  // Bare [1] / [1, 2] citation markers become links only when there is a source
  // list to resolve them against. Markers that are ALREADY markdown links (the
  // video list arrives as `[title](url)`) must be left alone — rewriting them
  // would corrupt the link.
  const withLinks = sources?.length
    ? body.replace(/\[(\d+(?:\s*,\s*\d+)*)\](?!\()/g, (_match, numberList) =>
        numberList
          .split(",")
          .map((n) => n.trim())
          .map((n) => {
            const source = sources.find((s) => s.index === Number(n));
            return source ? `[${n}](${source.url})` : `[${n}]`;
          })
          .join(" "),
      )
    : body;

  // Styling mirrors the FAQ tab's. Tables and headings matter here because GPT
  // answers are standard markdown — without borders a spec table renders as
  // bare rows with values and test methods running together.
  return (
    <div
      className="prose-chat
        [&>*]:max-w-none
        [&_p]:my-3 [&_p]:first:mt-0 [&_p]:leading-relaxed
        [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-6 [&_h1]:mb-2 [&_h1]:first:mt-0
        [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:first:mt-0
        [&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-1.5 [&_h3]:first:mt-0
        [&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:space-y-1.5
        [&_ol]:my-3 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:space-y-1.5
        [&_li]:leading-relaxed [&_li]:pl-1
        [&_strong]:font-semibold [&_strong]:text-black dark:[&_strong]:text-white
        [&_hr]:my-5 [&_hr]:border-black/10 dark:[&_hr]:border-white/10
        [&_table]:border-collapse [&_table]:my-4 [&_table]:w-full [&_table]:text-[13px]
        [&_thead]:bg-slate-100 dark:[&_thead]:bg-gray-700/60
        [&_th]:border [&_th]:border-slate-300 dark:[&_th]:border-gray-600 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:align-bottom
        [&_td]:border [&_td]:border-slate-300 dark:[&_td]:border-gray-600 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_td]:leading-6
        [&_tbody_tr:nth-child(even)]:bg-black/[0.02] dark:[&_tbody_tr:nth-child(even)]:bg-white/[0.03]"
    >
      {/* singleTilde off: spec ranges are written `0.5~11.0 mm (0.0197~0.4331
          inch)`, and GFM's default reads that tilde pair as strikethrough —
          a line through the middle of the value, with both tildes swallowed.
          `~~double~~` still strikes through. */}
      <ReactMarkdown
        remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
        components={{
          // Only the table scrolls sideways, not the whole answer: a wide spec
          // table should not make the prose around it horizontally scrollable.
          table: ({ children }) => (
            <div className="overflow-x-auto -mx-1 px-1 my-4">
              <table>{children}</table>
            </div>
          ),
          // A citation marker is a link whose whole text is its number; those get
          // the small superscript badge. Anything else is a real link with a
          // title of its own (a video, say) and has to stay readable as text.
          a: ({ href, children }) => {
            const label = Array.isArray(children)
              ? children.join("")
              : children;
            const isCitation =
              typeof label === "string" && /^\d+$/.test(label.trim());
            return (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={
                  isCitation
                    ? "inline-flex items-center justify-center align-super text-[0.7em] font-medium text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 rounded px-1 mx-0.5 no-underline"
                    : "text-orange-700 dark:text-orange-400 underline underline-offset-2 hover:text-orange-800 dark:hover:text-orange-300 break-words"
                }
              >
                {children}
              </a>
            );
          },
        }}
      >
        {withLinks}
      </ReactMarkdown>
      <SourcesFooter sources={sources} />
      <ActionLinks links={actionLinks} />
    </div>
  );
}

// A numbered, titled link list under the answer — same [n] numbering as the
// inline badges above, so a reader can jump straight to a citation without
// hunting through the prose for its badge. Titles/URLs only, no image cards
// (2026-09-01 scope decision, see frontend_ui_gap_findings_2026_09_01.md).
function SourcesFooter({ sources }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="mt-3 pt-2 border-t border-black/10 dark:border-white/10">
      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
        Sources
      </div>
      <ol className="text-xs space-y-0.5">
        {sources.map((s) => (
          <li key={s.index} className="flex gap-1.5">
            <span className="text-gray-400 dark:text-gray-500 shrink-0">
              [{s.index}]
            </span>
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer"
              className="text-orange-700 dark:text-orange-400 hover:underline truncate"
            >
              {s.title || s.domain || s.url}
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
}

// Required whenever GPT answered, and keyed on who actually answered rather than
// on the selected model: the backend routes complex questions to GPT even while
// the picker sits on Gemini, so a reader can receive a web-search answer without
// ever having chosen one. Saying nothing would let those read as database specs.
function WebSearchNotice({ worker, language }) {
  if (worker !== "gpt") return null;
  return (
    <p className="mt-2 text-[11px] leading-relaxed text-amber-700 dark:text-amber-400">
      {language === "zh"
        ? "此答案來自即時網路搜尋，非我們資料庫的官方規格，請自行確認引用來源。"
        : "This answer comes from a live web search, not our official database specs — please verify the cited sources yourself."}
    </p>
  );
}

// Root Agent's own stage values — see ../api/chat.js and spec §3. `querying` is the
// long one (Cloud SQL + translator + webhook), so its label warns that a complex
// question can take much longer instead of looking stuck.
export const STAGE_LABEL = {
  reading_attachments: { en: "Reading attachments…", zh: "正在讀取附件…" },
  routing: {
    en: "Understanding your question...",
    zh: "正在理解您的問題...",
  },
  querying: {
    en: "Looking up product data — a complex query can take up to a minute...",
    zh: "正在查詢產品資料——複雜查詢可能需要一分鐘...",
  },
  // Both come from the FAQ path, which any FAQ-routed question reaches — they
  // were missing before videos existed, falling back to the `querying` label
  // and telling people we were "looking up product data" during a web search.
  searching: {
    en: "Searching the web...",
    zh: "正在搜尋網路資料...",
  },
  synthesizing: {
    en: "Putting the answer together...",
    zh: "正在彙整答案...",
  },
  composing: {
    en: "Composing the answer...",
    zh: "正在整理答案...",
  },
  // Sent right before a GPT call starts (backend PR #27), so the wait can say
  // what is actually happening. Measured end-to-end at 57-85s, hence the
  // 1-2 minute wording rather than `querying`'s "up to a minute".
  web_searching: {
    en: "Searching tglobalcorp.com — this usually takes 1-2 minutes...",
    zh: "正在即時搜尋 T-Global 官網——通常需要 1-2 分鐘...",
  },
  error: {
    en: "Something went wrong, wrapping up...",
    zh: "發生問題，正在收尾...",
  },
};

function StageIndicator({ stage, language, attachmentTurn }) {
  const label =
    attachmentTurn && stage === "web_searching"
      ? { en: "Searching approved keywords…", zh: "正在查詢核准的短搜尋詞…" }
      : (STAGE_LABEL[stage] ?? STAGE_LABEL.querying);
  return (
    <div
      role="status"
      className="inline-flex items-center gap-2 text-orange-700 dark:text-orange-300 text-xs md:text-sm"
    >
      <BouncingDots size="sm" color="bg-orange-300" />
      {label[language] ?? label.en}
    </div>
  );
}

// Ticks its own local state so the running clock re-renders this span only, not
// the whole message list. Stops once elapsedMs is set.
function ElapsedTimer({ startedAt, elapsedMs }) {
  const [liveMs, setLiveMs] = useState(() => Date.now() - startedAt);

  useEffect(() => {
    if (elapsedMs != null) return;
    const id = setInterval(() => setLiveMs(Date.now() - startedAt), 200);
    return () => clearInterval(id);
  }, [startedAt, elapsedMs]);

  return (
    <span className="tabular-nums">
      {((elapsedMs ?? liveMs) / 1000).toFixed(1)}s
    </span>
  );
}

// MessageRenderer is a hand-rolled parser for the `table:` / `Source:` syntax
// product lookups reply with. Everything else on this tab — FAQ answers, the
// video list, GPT answers — is standard markdown, which it does not understand:
// headings and pipe tables stay as literal text, and its LaTeX pass turns things
// like `±0.3` into a stray `= 0.3`.
//
// So the question is not "does this look like markdown" but "does this use the
// in-house syntax", because only MessageRenderer can render that. Keying off
// markdown features instead was unreliable — the same model answering the same
// question rendered correctly or not depending on whether its reply happened to
// contain a link.
const INHOUSE_SYNTAX = /^\s*(table:|viz:)/im;

function needsMarkdown(message) {
  // Only `table:` / `viz:` decide this. A `Source:` block says where to find the
  // source buttons, not how the prose above it is written — GPT answers carry
  // both, and treating the block as a vote for MessageRenderer left their inline
  // markdown links printed as raw URLs.
  return !INHOUSE_SYNTAX.test(message.text ?? "");
}
